import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import {
  GetPharmaCompanyById,
  UpdatePharmaCompany
} from "../../../../api/medicalStore/master/pharmaCompanyAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  name: yup.string().required("Company Name is required"),
  shortName: yup.string().required("Short Name is required"),

  localCompanyDetails: yup.object().shape({
    contactPerson: yup.string().required("Local Contact Person is required"),
    mobile: yup.string().required("Local Mobile is required"),
    email: yup.string().email("Invalid email").required("Email is required")
  }),

  regionalCompanyDetails: yup.object().shape({
    contactPerson: yup.string().required("Regional Contact Person is required"),
    mobile: yup.string().required("Regional Mobile is required"),
    email: yup.string().email("Invalid email").required("Email is required")
  }),

  nationalCompanyDetails: yup.object().shape({
    contactPerson: yup.string().required("National Contact Person is required"),
    mobile: yup.string().required("National Mobile is required"),
    email: yup.string().email("Invalid email").required("Email is required")
  })
});

const EditPharmaCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const fetchData = async () => {
    try {
      const response = await GetPharmaCompanyById(id); // Make sure login function returns token
      
      if (response.success) {
        Object.keys(response.data).forEach(key => {
          setValue(key, response.data[key]);
        });
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  // Fetch existing data
  useEffect(
    () => {
      if (id) {
        fetchData();
      }
    },
    [id, setValue]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdatePharmaCompany(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

  /* ================= Reusable Section ================= */
  const CompanySection = ({ title, prefix }) => {
  const sectionErrors = errors?.[prefix] || {};

  return (
    <>
      <hr />
      <h6 className="mb-3">{title}</h6>
      <div className="row">
        <div className="col-4">
          <label>Contact Person</label>
          <input
            className="form-control"
            {...register(`${prefix}.contactPerson`)}
          />
          {sectionErrors.contactPerson && (
            <span className="text-danger">
              {sectionErrors.contactPerson.message}
            </span>
          )}
        </div>

        <div className="col-4">
          <label>Mobile</label>
          <input
            className="form-control"
            {...register(`${prefix}.mobile`)}
          />
          {sectionErrors.mobile && (
            <span className="text-danger">
              {sectionErrors.mobile.message}
            </span>
          )}
        </div>

        <div className="col-4">
          <label>Email</label>
          <input
            className="form-control"
            {...register(`${prefix}.email`)}
          />
          {sectionErrors.email && (
            <span className="text-danger">
              {sectionErrors.email.message}
            </span>
          )}
        </div>

        <div className="col-4 mt-2">
          <label>Phone (Office)</label>
          <input
            className="form-control"
            {...register(`${prefix}.phoneO`)}
          />
        </div>

        <div className="col-4 mt-2">
          <label>Phone (Residence)</label>
          <input
            className="form-control"
            {...register(`${prefix}.phoneR`)}
          />
        </div>

        <div className="col-4 mt-2">
          <label>Fax</label>
          <input
            className="form-control"
            {...register(`${prefix}.fax`)}
          />
        </div>

        <div className="col-12 mt-2">
          <label>Address</label>
          <textarea
            className="form-control"
            {...register(`${prefix}.address`)}
          />
        </div>
      </div>
    </>
  );
};

  return (
    <Fragment>
      <Breadcrumb parent="Master" title="Edit Pharma Company" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit Pharma Company</h5>
            <Link to={`/medical-store/master/pharma-company/list`}>
              <button className="btn btn-primary" id="list">
                Company List
              </button>
              <UncontrolledTooltip target="list">
                Company List
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
            <div className="card-body">
              {/* ===== Basic Info ===== */}
              <div className="row">
                <div className="col-6">
                  <label>Company Name</label>
                  <input
                    className="form-control"
                    {...register("name")}
                  />
                  <p className="text-danger">{errors.name?.message}</p>
                </div>

                <div className="col-6">
                  <label>Short Name</label>
                  <input
                    className="form-control"
                    {...register("shortName")}
                  />
                  <p className="text-danger">{errors.shortName?.message}</p>
                </div>
              </div>

              {/* ===== Company Sections ===== */}
              <CompanySection
                title="Local Company Details"
                prefix="localCompanyDetails"
              />

              <CompanySection
                title="Regional Company Details"
                prefix="regionalCompanyDetails"
              />

              <CompanySection
                title="National Company Details"
                prefix="nationalCompanyDetails"
              />
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary" type="submit">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditPharmaCompany;
